import { noteGoApiFetch } from '@/services/noteGoApi/noteGoApiFetcher';
import type { Comment, CreateCommentInput } from '../types';

const buildShareQuery = (shareToken?: string | null): string =>
  shareToken ? `?share_token=${encodeURIComponent(shareToken)}` : '';

/**
 * Fetch the comment tree for a note.
 * Authenticated callers go through the bearer-token fetcher; unauthenticated
 * public viewers read via the share token.
 */
export async function fetchComments({
  noteId,
  shareToken,
  isAuthenticated,
}: {
  noteId: string;
  shareToken?: string | null;
  isAuthenticated: boolean;
}): Promise<Comment[]> {
  const query = buildShareQuery(shareToken);

  if (isAuthenticated) {
    return (await noteGoApiFetch({
      url: `/notes/${noteId}/comments${query}`,
      method: 'GET',
    })) as Comment[];
  }

  const res = await fetch(`${import.meta.env.VITE_API_URL}/notes/${noteId}/comments${query}`);
  if (!res.ok) {
    throw new Error(`Failed to load comments (${res.status})`);
  }
  return (await res.json()) as Comment[];
}

/** Create a top-level comment or a reply (login required). */
export async function createComment({
  noteId,
  shareToken,
  input,
}: {
  noteId: string;
  shareToken?: string | null;
  input: CreateCommentInput;
}): Promise<Comment> {
  return (await noteGoApiFetch({
    url: `/notes/${noteId}/comments`,
    method: 'POST',
    body: {
      body: input.body,
      parent_id: input.parentId ?? null,
      video_time: input.videoTime ?? null,
      share_token: shareToken ?? null,
    },
  })) as Comment;
}

/** Edit a comment body (author only — enforced server-side). */
export async function updateComment({
  noteId,
  commentId,
  body,
}: {
  noteId: string;
  commentId: string;
  body: string;
}): Promise<Comment> {
  return (await noteGoApiFetch({
    url: `/notes/${noteId}/comments/${commentId}`,
    method: 'PUT',
    body: { body },
  })) as Comment;
}

/** Delete a comment (author or note owner — enforced server-side). */
export async function deleteComment({ noteId, commentId }: { noteId: string; commentId: string }): Promise<void> {
  await noteGoApiFetch({
    url: `/notes/${noteId}/comments/${commentId}`,
    method: 'DELETE',
  });
}
